import React, { useState, useEffect, useRef, useCallback } from "react";
import { BrandLogo } from "../brand/BrandLogo";
import OnboardingWizard from "../onboarding/OnboardingWizard";
import { HelpMenu } from "../onboarding/HelpMenu";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Building2, Users, FileSignature, Receipt,
  Wallet, MessagesSquare, Sparkles, LogOut, Bell, Search, Menu, X, ChevronDown,
  ShieldCheck, Wrench, Hammer, ShieldAlert, Inbox, FolderLock, Mail, CreditCard, Check, Loader2, LifeBuoy,
  Newspaper, GraduationCap, TrendingUp, Landmark, BookOpen, Compass, Calculator, ChevronRight, ClipboardCheck, Briefcase, FileText, UserCircle, CalendarClock, History
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { ActivityGate, ActivitySwitch } from "./ActivitySwitch";
import { StrLockedPreview } from "./StrLockedPreview";
import { Palmtree, CalendarDays, BedDouble, Tags, ClipboardList, Sparkles as SparklesIcon, Receipt as ReceiptIcon, Banknote as BanknoteIcon, BookOpenText, Scale, Globe2, KeyRound as KeyRoundIcon, FileBarChart, Lock as LockIcon } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import MfaGate from "../auth/MfaGate";
import ContactAdminModal from "./ContactAdminModal";
import { InstallBanner } from "./InstallBanner";
import { IosInstallGuide } from "./IosInstallGuide";

function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const notes = [880, 1174.66];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const start = ctx.currentTime + i * 0.15;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.18, start + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.32);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.34);
    });
    setTimeout(() => ctx.close().catch(() => {}), 900);
  } catch (e) { /* ignore */ }
}

function NotificationBell({ role }) {
  const isArtisan = role === "artisan";
  const isReferent = role === "referent";
  const INVESTOR_ROLES = ["bailleur", "gestionnaire", "locataire", "admin"];
  const isInvestor = INVESTOR_ROLES.includes(role);
  const active = isArtisan || isInvestor || isReferent;
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("notif_dismissed") || "[]"); } catch { return []; }
  });
  const [seenKeys, setSeenKeys] = useState(() => {
    try { return JSON.parse(localStorage.getItem("notif_seen") || "[]"); } catch { return []; }
  });
  const ref = useRef(null);
  const navigate = useNavigate();
  const prevCount = useRef(null);
  const prevDealCount = useRef(null);
  const prevEdlCount = useRef(null);
  const prevRefCount = useRef(null);
  const prevOwnerCount = useRef(null);
  const prevStrCount = useRef(null);

  const load = useCallback(() => {
    if (isArtisan) {
      api.get("/works/artisan-notifications").then((r) => {
        const newCount = r.data.count || 0;
        const its = r.data.items || [];
        if (prevCount.current !== null && newCount > prevCount.current) {
          const diff = newCount - prevCount.current;
          playChime();
          const latest = its.find((it) => !it.seen);
          toast.success(
            diff > 1 ? `${diff} nouvelles demandes reçues` : "Nouvelle demande reçue",
            { description: latest ? `${latest.client_name}${latest.domain ? " · " + latest.domain : ""}` : undefined }
          );
        }
        prevCount.current = newCount;
        setCount(newCount);
        setItems(its.map((it) => ({ key: it.id, kind: "work", title: it.client_name, sub: it.domain || it.description, seen: it.seen })));
      }).catch(() => {});
    } else if (isInvestor || isReferent) {
      api.get("/alerts").then(async (r) => {
        const edlsA = (r.data.edl_assigned || []).map((n) => ({ key: `edl-${n.id}`, kind: "deal", title: n.property_name || "État des lieux", sub: `Nouvel EDL ${n.type === "sortie" ? "de sortie" : "d'entrée"} confié${n.edl_date ? " · " + n.edl_date : ""}`, to: isReferent ? "/app/referent" : `/app/etats-des-lieux?edl=${n.edl_id}`, notifId: n.id }));
        const edlDocs = (r.data.edl_docs || []).map((n) => ({ key: `edoc-${n.id}`, kind: "deal", title: n.property_name || "Fin de bail", sub: `${n.files || ""} document(s) de fin de bail disponibles`, to: `/app/mes-documents`, notifId: n.id }));
        const edlEntry = (r.data.edl_tenant_entry || []).map((n) => ({ key: `eent-${n.id}`, kind: "deal", title: n.property_name || "Emménagement", sub: `État des lieux d'entrée planifié${n.edl_date ? " · " + n.edl_date : ""}`, to: `/app/mon-espace`, notifId: n.id }));
        const edlCount = edlsA.length;
        if (prevEdlCount.current !== null && edlCount > prevEdlCount.current) {
          const diff = edlCount - prevEdlCount.current;
          playChime();
          toast.success(diff > 1 ? `${diff} nouveaux états des lieux confiés` : "Nouvel état des lieux confié", { description: edlsA[0]?.title });
        }
        prevEdlCount.current = edlCount;
        const dealsM = (r.data.deal_matches || []).map((n) => ({ key: `dm-${n.id}`, kind: "deal", title: n.deal_title, sub: `Nouveau bon deal${n.city ? " · " + n.city : ""}${n.rentability ? " · " + n.rentability + " %" : ""}`, to: `/app/offres-deals?deal=${n.deal_id}`, notifId: n.id }));
        const dealCount = dealsM.length;
        if (prevDealCount.current !== null && dealCount > prevDealCount.current) {
          const diff = dealCount - prevDealCount.current;
          playChime();
          toast.success(diff > 1 ? `${diff} nouveaux bons deals` : "Nouveau bon deal", { description: dealsM[0]?.title });
        }
        prevDealCount.current = dealCount;
        const pass = (r.data.passoire || []).map((pr) => ({ key: `p-${pr.id}`, kind: "passoire", title: pr.name, sub: `DPE · ${(pr.reasons || []).join(" · ") || pr.energy_class}`, to: `/app/biens/${pr.id}` }));
        const taskDue = (r.data.task_due || []).map((n) => ({ key: `td-${n.id}`, kind: "doc", title: n.title, sub: n.body, to: n.url, notifId: n.id }));
        const docs = (r.data.expiring_docs || []).map((d) => ({ key: `d-${d.id}`, kind: "doc", title: d.label, sub: `${d.category ? d.category + " · " : ""}${d.property_name} · ${d.days < 0 ? "Expiré" : `Expire dans ${d.days} j`}`, to: `/app/biens/${d.property_id}` }));
        const pieces = (r.data.expiring_pieces || []).map((pc) => ({ key: `pc-${pc.id}`, kind: "doc", title: pc.name, sub: `${pc.piece_type} · ${pc.days < 0 ? "Pièce expirée" : `${pc.days} j`}`, to: `/app/locataires/${pc.id}` }));
        const ending = (r.data.ending_leases || []).map((l) => ({ key: `le-${l.id}`, kind: "doc", title: l.property, sub: `Fin de bail · ${l.days < 0 ? "Échu" : `${l.days} j`}`, to: `/app/locations` }));
        const revisions = (r.data.revision_reminders || []).map((rv) => ({ key: `rv-${rv.id}`, kind: "doc", title: rv.property, sub: `Révision de loyer · ${rv.days === 0 ? "aujourd'hui" : `${rv.days} j`}`, to: `/app/locations` }));
        const recs = (r.data.recurring_generated || []).map((n) => ({ key: `rec-${n.id}`, kind: "recurring", title: n.label, sub: `Charge générée · ${(n.amount || 0).toLocaleString("fr-FR")} € · joindre la facture`, to: `/app/finances?tx=${n.transaction_id}&notif=${n.id}`, notifId: n.id }));
        const loansEnd = (r.data.ending_loans || []).map((l) => ({ key: `ln-${l.id}`, kind: "doc", title: `${l.type}${l.payer_to ? ` · ${l.payer_to}` : ""}`, sub: `Prêt · ${l.days < 0 ? "soldé" : (l.months_left <= 1 ? "dernière échéance" : `${l.months_left} échéances`)}`, to: `/app/finances/prets` }));
        const loansIns = (r.data.loan_insurance_reviews || []).map((l) => ({ key: `li-${l.id}`, kind: "doc", title: `${l.type}${l.payer_to ? ` · ${l.payer_to}` : ""}`, sub: `Assurance emprunteur · ${(l.annual_insurance || 0).toLocaleString("fr-FR")} €/an · à renégocier`, to: `/app/finances/prets` }));
        const loansPay = (r.data.loan_payment_reminders || []).map((l) => ({ key: `lp-${l.id}`, kind: "doc", title: `${l.type}${l.payer_to ? ` · ${l.payer_to}` : ""}`, sub: `Prélèvement · ${(l.amount || 0).toLocaleString("fr-FR")} € dans ${l.days} j · vérifier la provision`, to: `/app/finances/prets` }));
        const deposits = (r.data.unpaid_deposits || []).map((d) => ({ key: `dep-${d.id}`, kind: "doc", title: `Dépôt de garantie · ${d.tenant || "—"}`, sub: `${(d.amount || 0).toLocaleString("fr-FR")} € en attente${d.property ? " · " + d.property : ""} · à encaisser`, to: `/app/finances` }));
        const all = [...taskDue, ...edlsA, ...edlDocs, ...edlEntry, ...dealsM, ...recs, ...deposits, ...pass, ...docs, ...pieces, ...ending, ...revisions, ...loansEnd, ...loansIns, ...loansPay];
        if (isReferent) {
          try {
            const rn = await api.get("/referents/notifications");
            const missionItems = (rn.data.missions || []).map((m) => ({ key: `rm-${m.id}`, kind: "work", title: `Mission · ${m.title || ""}`, sub: `${m.owner_name || ""}${m.property_name ? " · " + m.property_name : ""}${m.amount ? " · " + Number(m.amount).toLocaleString("fr-FR") + " €" : ""}`, to: `/app/referents?mission=${m.mission_id || m.id}` }));
            const msgItems = (rn.data.messages || []).map((x) => ({ key: `rmsg-${x.id}`, kind: "deal", title: `Message · ${x.sender_name || ""}`, sub: (x.body || "").slice(0, 60), to: `/app/referents?mission=${x.mission_id}` }));
            const revItems = (rn.data.reviews || []).map((x) => ({ key: `rev-${x.id}`, kind: "deal", title: `Nouvel avis · ${x.rating ?? ""}/5`, sub: `Évaluation reçue de ${x.author || "un propriétaire"}`, to: `/app/referents?mission=${x.mission_id}` }));
            const refCount = missionItems.length + msgItems.length + revItems.length;
            const contractItems = rn.data.contract ? [{ key: "rc-contract", kind: "work", title: rn.data.contract.title, sub: "Validez et signez votre contrat de partenariat", to: "/app/referent-profil" }] : [];
            const totalRef = refCount + contractItems.length;
            if (prevRefCount.current !== null && totalRef > prevRefCount.current) {
              playChime();
              toast.success("Nouvelle activité référent", { description: (contractItems[0] || missionItems[0] || revItems[0] || msgItems[0])?.sub });
            }
            prevRefCount.current = totalRef;
            all.unshift(...contractItems, ...missionItems, ...revItems, ...msgItems);
          } catch (e) { /* ignore */ }
        }
        if (isInvestor && !isReferent) {
          try {
            const on = await api.get("/referents/owner-notifications");
            const oItems = (on.data.items || []).map((x) => ({ key: `om-${x.mission_id}`, kind: "work", title: x.title, sub: x.sub, to: `/app/referent-missions?mission=${x.mission_id}` }));
            if (prevOwnerCount.current !== null && oItems.length > prevOwnerCount.current) {
              playChime();
              toast.success("Mise à jour d'une mission référent", { description: oItems[0]?.title });
            }
            prevOwnerCount.current = oItems.length;
            all.unshift(...oItems);
          } catch (e) { /* ignore */ }
        }
        if (isInvestor && !isReferent) {
          try {
            const sn = await api.get("/str/notifications");
            const sItems = (sn.data || []).map((x) => ({ key: `sn-${x.id}`, kind: "deal", title: x.title, sub: x.body, to: x.url, notifId: x.id }));
            if (prevStrCount.current !== null && sItems.length > prevStrCount.current) { playChime(); toast.success(sItems[0]?.title || "Location saisonnière", { description: sItems[0]?.sub }); }
            prevStrCount.current = sItems.length;
            all.unshift(...sItems);
          } catch (e) { /* ignore */ }
        }
        setCount(all.length);
        setItems(all);
      }).catch(() => {});
    }
  }, [isArtisan, isInvestor, isReferent]);

  useEffect(() => {
    if (!active) return;
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [active, load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = () => {
    setOpen(!open);
  };

  const clickItem = (it) => {
    setOpen(false);
    if (isArtisan) navigate("/app/travaux");
    else if (it.to) navigate(it.to);
  };

  const dismiss = (it, e) => {
    e.stopPropagation();
    const nd = [...new Set([...dismissed, it.key])];
    setDismissed(nd);
    try { localStorage.setItem("notif_dismissed", JSON.stringify(nd)); } catch {}
    if (it.notifId) api.post(`/notifications/${it.notifId}/read`).catch(() => {});
  };

  const visibleItems = items.filter((it) => !dismissed.includes(it.key));
  const unreadCount = visibleItems.filter((it) => !seenKeys.includes(it.key)).length;

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (prevOpenRef.current && !open) {
      setSeenKeys((prev) => {
        const keys = [...new Set([...prev, ...items.filter((it) => !dismissed.includes(it.key)).map((it) => it.key)])];
        try { localStorage.setItem("notif_seen", JSON.stringify(keys)); } catch {}
        return keys;
      });
    }
    prevOpenRef.current = open;
  }, [open, items, dismissed]);

  const clearAll = (e) => {
    e.stopPropagation();
    const nd = [...new Set([...dismissed, ...visibleItems.map((it) => it.key)])];
    setDismissed(nd);
    try { localStorage.setItem("notif_dismissed", JSON.stringify(nd)); } catch {}
    visibleItems.forEach((it) => { if (it.notifId) api.post(`/notifications/${it.notifId}/read`).catch(() => {}); });
  };

  const title = isArtisan ? "Nouvelles demandes" : "Alertes";
  const empty = isArtisan ? "Aucune demande pour le moment." : "Aucune alerte. Tout est à jour. 👍";

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell-btn"
        onClick={toggle}
        className="relative w-10 h-10 rounded-full bg-brand-softer flex items-center justify-center hover:bg-brand-soft transition-colors"
      >
        <Bell className="w-5 h-5 text-brand-green" />
        {active && unreadCount > 0 && (
          <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
        {!active && <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-red-500" />}
      </button>

      {open && active && (
        <div data-testid="notification-panel" className="absolute right-0 mt-2 w-80 bg-white rounded-2xl border border-brand-soft shadow-xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-brand-soft flex items-center gap-2">
            <Inbox className="w-4 h-4 text-brand-green" />
            <span className="font-semibold text-sm text-brand-greenDeep">{title}</span>
            {visibleItems.length > 0 && (
              <button onClick={clearAll} data-testid="notification-clear-all" className="ml-auto text-xs font-semibold text-brand-green/50 hover:text-red-500 transition-colors">Tout effacer</button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {visibleItems.length === 0 && (
              <p className="px-4 py-6 text-center text-sm text-brand-green/50">{empty}</p>
            )}
            {visibleItems.map((it) => { const unread = !seenKeys.includes(it.key); return (
              <div
                key={it.key}
                data-testid={`notification-item-${it.key}`}
                data-unread={unread ? "true" : "false"}
                onClick={() => clickItem(it)}
                className={`w-full text-left px-4 py-3 border-b border-brand-soft last:border-0 hover:bg-brand-softer transition-colors cursor-pointer flex items-start gap-2 ${it.kind === "passoire" ? "bg-red-50/40" : it.kind === "doc" ? "bg-amber-50/40" : it.kind === "deal" ? "bg-brand-lime/15" : it.kind === "recurring" ? "bg-brand-lime/10" : (unread ? "bg-brand-lime/10" : "")} ${unread ? "" : "opacity-55"}`}
              >
                {unread && <span className="mt-1.5 w-2 h-2 rounded-full bg-red-500 shrink-0" data-testid={`notification-unread-dot-${it.key}`} />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm text-brand-greenDeep ${unread ? "font-semibold" : "font-medium"}`}>{it.title}</p>
                  </div>
                  {it.sub && <p className="text-xs text-brand-green/60 truncate">{it.sub}</p>}
                </div>
                <button onClick={(e) => dismiss(it, e)} title="Supprimer l'alerte" data-testid={`notification-dismiss-${it.key}`} className="text-brand-green/30 hover:text-red-500 transition-colors shrink-0 mt-0.5"><X className="w-3.5 h-3.5" /></button>
              </div>
            ); })}
          </div>
        </div>
      )}
    </div>
  );
}

const MENU_ITEMS = {
  "/app": { to: "/app", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  "/app/biens": { to: "/app/biens", label: "Biens", icon: Building2 },
  "/app/locataires": { to: "/app/locataires", label: "Locataires", icon: Users },
  "/app/locations": { to: "/app/locations", label: "Locations", icon: FileSignature },
  "/app/quittances": { to: "/app/quittances", label: "Quittances", icon: Receipt },
  "/app/journal-envois": { to: "/app/journal-envois", label: "Journal des envois", icon: Mail },
  "/app/coffre": { to: "/app/coffre", label: "Coffre-fort", icon: FolderLock },
  "/app/etats-des-lieux": { to: "/app/etats-des-lieux", label: "États des lieux", icon: ClipboardCheck },
  "/app/etats-des-lieux#ref": { to: "/app/etats-des-lieux", label: "États des lieux", icon: ClipboardCheck },
  "/app/edl-historique": { to: "/app/edl-historique", label: "Historique EDL", icon: History },
  "/app/referent": { to: "/app/referent", label: "Mes états des lieux", icon: ClipboardCheck },
  "/app/referents": { to: "/app/referents", label: "Annuaire des référents", icon: Users },
  "/app/referent-missions": { to: "/app/referent-missions", label: "Mes missions", icon: Briefcase },
  "/app/referent-releves": { to: "/app/referent-releves", label: "Mes relevés", icon: FileText },
  "/app/referent-profil": { to: "/app/referent-profil", label: "Mon profil référent", icon: UserCircle },
  "/app/referent-messagerie": { to: "/app/referent-messagerie", label: "Messagerie", icon: Mail },
  "/app/referent-calendrier": { to: "/app/referent-calendrier", label: "Calendrier", icon: CalendarClock },
  "/app/referent-dashboard": { to: "/app/referent-dashboard", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  "/app/finances": { to: "/app/finances", label: "Finances", icon: Wallet },
  "/app/messagerie": { to: "/app/messagerie", label: "Messagerie", icon: MessagesSquare },
  "/app/assistant-ia": { to: "/app/assistant-ia", label: "Assistant IA", icon: Sparkles, badge: "IA" },
  "/app/assurances": { to: "/app/assurances", label: "Assurances", icon: ShieldCheck },
  "/app/artisans": { to: "/app/artisans", label: "Artisans", icon: Wrench },
  "/app/travaux": { to: "/app/travaux", label: "Travaux", icon: Hammer },
  "/app/mon-espace": { to: "/app/mon-espace", label: "Mon espace", icon: LayoutDashboard, end: true },
  "/app/mes-assurances": { to: "/app/mes-assurances", label: "Mes assurances", icon: Receipt },
  "/app/espace-assureur": { to: "/app/espace-assureur", label: "Mes produits", icon: ShieldCheck },
  "/app/activite": { to: "/app/activite", label: "Mon activité", icon: LayoutDashboard, end: true },
  "/app/admin": { to: "/app/admin", label: "Administration", icon: ShieldAlert },
  "/app/actualites": { to: "/app/actualites", label: "Actualités", icon: Newspaper },
  "/app/formation": { to: "/app/formation", label: "Formation", icon: GraduationCap },
  "/app/coach": { to: "/app/coach", label: "Mon Coach", icon: Compass },
  "/app/offres-deals": { to: "/app/offres-deals", label: "Offres & bons deals", icon: TrendingUp },
  "/app/simulateur": { to: "/app/simulateur", label: "Simulateur d'investissement", icon: Calculator },
  "/app/financement": { to: "/app/financement", label: "Offre de financement", icon: Landmark },
  "/app/articles": { to: "/app/articles", label: "Articles", icon: BookOpen },
  "/app/saisonnier": { to: "/app/saisonnier", label: "Tableau saisonnier", icon: LayoutDashboard, end: true },
  "/app/saisonnier/calendrier": { to: "/app/saisonnier/calendrier", label: "Calendrier", icon: CalendarDays },
  "/app/saisonnier/reservations": { to: "/app/saisonnier/reservations", label: "Réservations", icon: BedDouble },
  "/app/saisonnier/tarifs": { to: "/app/saisonnier/tarifs", label: "Tarifs & messages", icon: Tags },
  "/app/saisonnier/conformite": { to: "/app/saisonnier/conformite", label: "Conformité", icon: ClipboardList },
  "/app/saisonnier/missions": { to: "/app/saisonnier/missions", label: "Missions & ménage", icon: SparklesIcon },
  "/app/saisonnier/taxe-sejour": { to: "/app/saisonnier/taxe-sejour", label: "Taxe de séjour", icon: ReceiptIcon },
  "/app/saisonnier/reservation-directe": { to: "/app/saisonnier/reservation-directe", label: "Réservation directe", icon: Globe2 },
  "/app/saisonnier/rapports": { to: "/app/saisonnier/rapports", label: "Rapports", icon: FileBarChart },
  "/app/saisonnier/serrures": { to: "/app/saisonnier/serrures", label: "Serrures connectées", icon: KeyRoundIcon },
  "/app/saisonnier/versements": { to: "/app/saisonnier/versements", label: "Versements plateformes", icon: BanknoteIcon },
  "/app/saisonnier/livret": { to: "/app/saisonnier/livret", label: "Livret d'accueil", icon: BookOpenText },
  "/app/saisonnier/fiscalite": { to: "/app/saisonnier/fiscalite", label: "Fiscalité meublé", icon: Scale },
};

// Rôles "gestion" qui utilisent le menu regroupé en catégories
const MGMT_ROLES = ["bailleur", "agent", "gestionnaire", "courtier", "admin"];

const STR_KEYS = ["/app/saisonnier", "/app/saisonnier/calendrier", "/app/saisonnier/reservations", "/app/saisonnier/reservation-directe", "/app/saisonnier/missions", "/app/saisonnier/serrures", "/app/saisonnier/tarifs", "/app/saisonnier/taxe-sejour", "/app/saisonnier/versements", "/app/saisonnier/rapports", "/app/saisonnier/fiscalite", "/app/saisonnier/livret", "/app/saisonnier/conformite"];
// Arborescence du menu regroupé (catégories repliables)
const MENU_TREE = [
  { type: "item", key: "/app" },
  { type: "cat", key: "cat_actualites", label: "Actualités immobilières", icon: Newspaper, children: ["/app/actualites"] },
  { type: "cat", key: "cat_formation", label: "Formation & accompagnement", icon: GraduationCap, children: ["/app/formation", "/app/coach"] },
  { type: "cat", key: "cat_investissement", label: "Investissement immobilier", icon: TrendingUp, children: ["/app/offres-deals", "/app/simulateur"] },
  { type: "cat", key: "cat_gestion", label: "Gestion immobilière", icon: Building2, children: ["/app/biens", "/app/locataires", "/app/locations", "/app/quittances", "/app/etats-des-lieux", "/app/edl-historique", "/app/journal-envois", "/app/coffre", "/app/messagerie", "/app/assistant-ia", "/app/finances"] },
  { type: "cat", key: "cat_saisonnier", label: "Location saisonnière", icon: Palmtree, children: STR_KEYS },
  { type: "cat", key: "cat_referent", label: "Référent local", icon: Users, children: ["/app/referents", "/app/referent-missions", "/app/referent-releves", "/app/referent-profil", "/app/etats-des-lieux#ref"] },
  { type: "cat", key: "cat_financement", label: "Financement & Assurance", icon: Landmark, children: ["/app/financement", "/app/assurances"] },
  { type: "cat", key: "cat_travaux", label: "Travaux d'Entretien & Réparations", icon: Hammer, children: ["/app/artisans", "/app/travaux", "/app/articles"] },
  { type: "item", key: "/app/admin" },
];

const _MGMT_MENU = [
  "/app",
  "cat_actualites", "/app/actualites",
  "cat_formation", "/app/formation", "/app/coach",
  "cat_investissement", "/app/offres-deals", "/app/simulateur",
  "cat_gestion", "/app/biens", "/app/locataires", "/app/locations", "/app/quittances",
  "/app/etats-des-lieux", "/app/edl-historique", "/app/journal-envois", "/app/coffre", "/app/messagerie", "/app/assistant-ia", "/app/finances",
  "cat_referent", "/app/referents", "/app/referent-missions", "/app/referent-releves", "/app/referent-profil",
  "cat_financement", "/app/financement", "/app/assurances",
  "cat_travaux", "/app/artisans", "/app/travaux", "/app/articles",
];

const DEFAULT_MENU = {
  bailleur: _MGMT_MENU.filter((k) => k !== "/app/assistant-ia"),
  agent: [..._MGMT_MENU],
  gestionnaire: [..._MGMT_MENU],
  courtier: _MGMT_MENU.filter((k) => k !== "/app/assistant-ia"),
  locataire: ["/app/mon-espace", "/app/messagerie", "/app/mes-documents", "/app/assurances", "/app/mes-assurances", "/app/artisans", "/app/travaux", "/app/assistant-ia"],
  assureur: ["/app/espace-assureur", "/app/assurances"],
  artisan: ["/app/activite", "/app/artisans", "/app/travaux"],
  referent: ["/app/referent-dashboard", "/app/referent-calendrier", "/app/referent-messagerie", "/app/referent", "/app/referent-releves", "/app/referent-profil"],
  admin: [..._MGMT_MENU, "/app/admin"],
};

const NAV_BY_ROLE = {
  bailleur: [
    { to: "/app", label: "Tableau de bord", icon: LayoutDashboard, end: true },
    { to: "/app/biens", label: "Biens", icon: Building2 },
    { to: "/app/locataires", label: "Locataires", icon: Users },
    { to: "/app/locations", label: "Locations", icon: FileSignature },
    { to: "/app/quittances", label: "Quittances", icon: Receipt },
    { to: "/app/journal-envois", label: "Journal des envois", icon: Mail },
    { to: "/app/coffre", label: "Coffre-fort", icon: FolderLock },
    { to: "/app/finances", label: "Finances", icon: Wallet },
    { to: "/app/messagerie", label: "Messagerie", icon: MessagesSquare },
    { to: "/app/assistant-ia", label: "Assistant IA", icon: Sparkles, badge: "IA" },
    { to: "/app/assurances", label: "Assurances", icon: ShieldCheck },
    { to: "/app/artisans", label: "Artisans", icon: Wrench },
    { to: "/app/travaux", label: "Travaux", icon: Hammer },
  ],
  locataire: [
    { to: "/app/mon-espace", label: "Mon espace", icon: LayoutDashboard, end: true },
    { to: "/app/mes-documents", label: "Mes documents", icon: ClipboardCheck },
    { to: "/app/assurances", label: "Assurances", icon: ShieldCheck },
    { to: "/app/mes-assurances", label: "Mes assurances", icon: Receipt },
    { to: "/app/artisans", label: "Artisans", icon: Wrench },
    { to: "/app/travaux", label: "Mes travaux", icon: Hammer },
    { to: "/app/assistant-ia", label: "Assistant IA", icon: Sparkles, badge: "IA" },
  ],
  assureur: [
    { to: "/app/espace-assureur", label: "Mes produits", icon: ShieldCheck },
  ],
  artisan: [
    { to: "/app/activite", label: "Mon activité", icon: LayoutDashboard, end: true },
    { to: "/app/artisans", label: "Mon profil", icon: Wrench },
    { to: "/app/travaux", label: "Demandes reçues", icon: Hammer },
  ],
  admin: [
    { to: "/app/admin", label: "Administration", icon: ShieldAlert },
  ],
};

const TITLES = {
  "/app": "Tableau de bord",
  "/app/edl-historique": "Historique des états des lieux",
  "/app/biens": "Mes biens",
  "/app/locataires": "Mes locataires",
  "/app/locations": "Mes locations",
  "/app/quittances": "Quittances",
  "/app/modeles-documents": "Modèles de documents",
  "/app/journal-envois": "Journal des envois",
  "/app/mon-espace": "Mon espace",
  "/app/finances": "Finances",
  "/app/finances/prets": "Prêts immobiliers",
  "/app/finances/rapprochement": "Rapprochement bancaire",
  "/app/messagerie": "Messagerie",
  "/app/assistant-ia": "Assistant IA",
  "/app/assurances": "Marketplace assurance",
  "/app/mes-assurances": "Mes assurances",
  "/app/espace-assureur": "Espace assureur",
  "/app/artisans": "Artisans du bâtiment",
  "/app/travaux": "Travaux",
  "/app/activite": "Mon activité",
  "/app/admin": "Administration",
  "/app/actualites": "Actualités immobilières",
  "/app/formation": "Formation",
  "/app/coach": "Mon Coach",
  "/app/offres-deals": "Offres sélectionnées & bons deals",
  "/app/simulateur": "Simulateur d'investissement",
  "/app/financement": "Offre de financement",
  "/app/articles": "Articles",
  "/app/referents": "Annuaire des référents",
  "/app/referent-missions": "Mes missions référent",
  "/app/referent-releves": "Mes relevés",
  "/app/referent-profil": "Mon profil référent",
  "/app/referent-messagerie": "Messagerie",
  "/app/referent-calendrier": "Calendrier des rendez-vous",
  "/app/referent-dashboard": "Tableau de bord",
  "/app/referent": "Mes états des lieux",
  "/app/saisonnier": "Location saisonnière",
  "/app/saisonnier/calendrier": "Calendrier saisonnier",
  "/app/saisonnier/reservations": "Réservations",
  "/app/saisonnier/tarifs": "Tarifs & messages",
  "/app/saisonnier/conformite": "Conformité saisonnière",
  "/app/saisonnier/missions": "Missions & ménage",
  "/app/saisonnier/taxe-sejour": "Taxe de séjour",
  "/app/saisonnier/versements": "Versements plateformes",
  "/app/saisonnier/reservation-directe": "Réservation directe",
  "/app/saisonnier/serrures": "Serrures connectées",
  "/app/saisonnier/rapports": "Rapport mensuel",
  "/app/saisonnier/livret": "Livret d'accueil",
  "/app/saisonnier/fiscalite": "Fiscalité meublé de tourisme",
};

function PlanChoiceGate({ user, setUser }) {
  const [offers, setOffers] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/offers").then((r) => setOffers(r.data.offers || [])).catch(() => {}); }, []);
  const choose = async (name) => {
    setBusy(name);
    try {
      const res = await api.put("/auth/plan", { plan: name });
      setUser(res.data);
      toast.success(`Offre « ${name} » activée`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };
  const fp = (n) => (!n ? "0€" : `${Number(n).toLocaleString("fr-FR", { minimumFractionDigits: n % 1 ? 2 : 0 })}€`);
  return (
    <div className="min-h-screen bg-brand-softer p-6 flex flex-col items-center justify-center" data-testid="plan-choice-gate">
      <div className="text-center max-w-2xl mb-8">
        <h1 className="font-display text-3xl font-semibold text-brand-greenDeep">Bienvenue sur 360immoinvest 👋</h1>
        <p className="text-brand-green/60 mt-2">Choisissez votre formule pour commencer. Vous pourrez en changer à tout moment depuis « Mon abonnement ».</p>
        <div className="mt-5 inline-flex gap-2 p-1 bg-white rounded-full">
          <button onClick={() => setCycle("monthly")} className={`px-5 py-2 rounded-full text-sm font-semibold ${cycle === "monthly" ? "bg-brand-lime text-brand-greenDeep" : "text-brand-green/55"}`} data-testid="gate-cycle-monthly">Mensuel</button>
          <button onClick={() => setCycle("yearly")} className={`px-5 py-2 rounded-full text-sm font-semibold ${cycle === "yearly" ? "bg-brand-lime text-brand-greenDeep" : "text-brand-green/55"}`} data-testid="gate-cycle-yearly">Annuel</button>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-5 max-w-5xl w-full">
        {offers.map((o) => {
          const price = cycle === "monthly" ? o.price_monthly : o.price_yearly;
          return (
            <div key={o.id || o.name} className={`rounded-3xl p-6 flex flex-col ${o.highlight ? "bg-brand-greenDeep text-white" : "bg-white border border-brand-soft"}`} data-testid={`gate-offer-${o.name}`}>
              <h3 className={`font-display text-xl font-semibold ${o.highlight ? "text-white" : "text-brand-greenDeep"}`}>{o.name}</h3>
              <div className="mt-3 flex items-end gap-1">
                <span className={`font-display text-4xl font-bold ${o.highlight ? "text-white" : "text-brand-greenDeep"}`}>{fp(price)}</span>
                <span className={`mb-1.5 text-xs ${o.highlight ? "text-white/60" : "text-brand-green/50"}`}>{!price ? "/ pour toujours" : cycle === "monthly" ? "/ mois" : "/ an"}</span>
              </div>
              <ul className="mt-4 space-y-2 flex-1">
                {(o.services || []).slice(0, 6).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm"><Check className={`w-4 h-4 mt-0.5 shrink-0 ${o.highlight ? "text-brand-lime" : "text-brand-emerald"}`} /><span className={o.highlight ? "text-white/85" : "text-brand-green/75"}>{f}</span></li>
                ))}
              </ul>
              <button onClick={() => choose(o.name)} disabled={busy === o.name} className={`mt-5 w-full py-3 rounded-full font-semibold disabled:opacity-60 ${o.highlight ? "bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep" : "bg-brand-soft hover:bg-brand-mint text-brand-greenDeep"}`} data-testid={`gate-choose-${o.name}`}>
                {busy === o.name ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Choisir cette offre"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UpgradePrompt({ q, onClose }) {
  const navigate = useNavigate();
  return (
    <div className="fixed inset-0 z-[80] bg-brand-greenDeep/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="upgrade-prompt">
      <div className="bg-white rounded-3xl max-w-md w-full p-7">
        <p className="text-xs uppercase tracking-[0.2em] text-brand-limeDark font-semibold">Nouveau bien</p>
        <h3 className="font-display text-2xl text-brand-greenDeep mt-2">Votre offre « {q.plan} » est limitée à {q.limit} bien{q.limit > 1 ? "s" : ""}</h3>
        <p className="text-sm text-brand-green/70 mt-2">Vous gérez déjà {q.count} bien{q.count > 1 ? "s" : ""}. Passez à l'offre supérieure pour ajouter ce nouveau bien avec le tutoriel guidé.</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={() => { onClose(); navigate("/app/profil"); }} className="px-5 py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm" data-testid="upgrade-prompt-cta">Voir les offres</button>
          <button onClick={onClose} className="px-5 py-3 rounded-full border border-brand-soft text-sm text-brand-greenDeep" data-testid="upgrade-prompt-close">Plus tard</button>
        </div>
      </div>
    </div>
  );
}

function OnboardingHost({ user, ready }) {
  const [wiz, setWiz] = useState(null);
  const [quota, setQuota] = useState(null);
  useEffect(() => {
    if (user?.role !== "bailleur") return undefined;
    if (ready) api.get("/onboarding/state").then(({ data }) => { if (data.show_wizard) setWiz(data.wizard); }).catch(() => {});
    const fresh = () => api.get("/onboarding/property-quota").then(({ data }) => (data.can_add ? setWiz({ step: 1, data: {}, fresh: true }) : setQuota(data))).catch(() => setWiz({ step: 1, data: {}, fresh: true }));
    const h = (e) => (e.detail?.fresh ? fresh() : api.get("/onboarding/state").then(({ data }) => setWiz({ ...data.wizard, step: e.detail?.step ?? data.wizard.step ?? 0 })).catch(() => setWiz({ step: e.detail?.step || 0 })));
    window.addEventListener("open-onboarding", h);
    return () => window.removeEventListener("open-onboarding", h);
  }, [user?.role, user?.user_id, ready]);
  if (quota) return <><HelpMenu /><UpgradePrompt q={quota} onClose={() => setQuota(null)} /></>;
  if (!wiz) return user?.role === "bailleur" ? <HelpMenu /> : null;
  return <OnboardingWizard key={wiz.fresh ? "fresh" : "main"} initial={wiz} onClose={() => { setWiz(null); window.dispatchEvent(new Event("onboarding-closed")); }} />;
}

export default function DashboardLayout() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [menuConfig, setMenuConfig] = useState(null);
  const [strAccess, setStrAccess] = useState(null);
  useEffect(() => { api.get("/str/access").then((r) => setStrAccess(!!r.data.has_access)).catch(() => setStrAccess(true)); }, [user?.plan]);
  const [msgUnread, setMsgUnread] = useState({ messagerie: 0, referent: 0 });
  const [edlCfg, setEdlCfg] = useState(null);
  const [openCats, setOpenCats] = useState({});

  useEffect(() => {
    if (!user || user.mfa_pending) return;
    api.get("/menu-config").then((r) => setMenuConfig(r.data.config || null)).catch(() => {});
    api.get("/edl/module-config").then((r) => setEdlCfg(r.data)).catch(() => {});
  }, [user?.id, user?.email, user?.role, user?.mfa_pending]);

  useEffect(() => {
    if (!user || user.mfa_pending) return;
    let stop = false;
    const fetchUnread = () => api.get("/messages/unread-count").then((r) => { if (!stop) setMsgUnread(r.data || {}); }).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    const onFocus = () => fetchUnread();
    window.addEventListener("focus", onFocus);
    return () => { stop = true; clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [user?.id, user?.mfa_pending, location.pathname]);

  const msgUnreadFor = (to) =>
    to === "/app/messagerie" ? (msgUnread.messagerie || 0)
    : to === "/app/referent-messagerie" ? (msgUnread.referent || 0)
    : 0;

  const role = user?.role || "bailleur";
  const accessStatus = user?.access_status || "approved";
  const needsPlan = role === "bailleur" && user?.plan_selected === false;
  if (user?.mfa_pending) return <MfaGate />;
  if (role !== "admin" && (accessStatus === "pending" || accessStatus === "revoked")) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-softer p-6" data-testid="access-blocked-screen">
        <div className="max-w-md w-full bg-white rounded-3xl border border-brand-soft p-8 text-center">
          <div className={`w-14 h-14 rounded-2xl mx-auto flex items-center justify-center ${accessStatus === "revoked" ? "bg-red-100 text-red-600" : "bg-brand-lime/30 text-brand-greenDeep"}`}>
            <ShieldAlert className="w-7 h-7" />
          </div>
          <h1 className="font-display text-2xl font-semibold text-brand-greenDeep mt-4">
            {accessStatus === "revoked" ? "Accès révoqué" : "Accès en attente d'autorisation"}
          </h1>
          <p className="text-sm text-brand-green/60 mt-2">
            {accessStatus === "revoked"
              ? "Votre accès à 360immoinvest a été révoqué par l'administrateur. Contactez-le pour rétablir votre compte."
              : "Votre compte a bien été créé. Un administrateur doit valider votre accès avant que vous puissiez utiliser l'application. Vous recevrez une confirmation prochainement."}
          </p>
          <p className="text-xs text-brand-green/40 mt-4">Connecté en tant que {user?.email}</p>
          <button onClick={logout} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold text-sm" data-testid="access-blocked-logout">
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }
  if (needsPlan) {
    return <PlanChoiceGate user={user} setUser={setUser} />;
  }
  const visibleKeys0 = (menuConfig && menuConfig[role]) || DEFAULT_MENU[role] || DEFAULT_MENU.bailleur;
  const edlHidden = edlCfg && !edlCfg.has_access;
  const EDL_KEYS = ["/app/etats-des-lieux", "/app/referent"];
  let visibleKeys = edlHidden ? visibleKeys0.filter((k) => !EDL_KEYS.includes(k)) : visibleKeys0;
  // Le référent gère ses EDL directement dans « Mes états des lieux » : on masque la page autonome États des lieux
  if (role === "referent") visibleKeys = visibleKeys.filter((k) => k !== "/app/etats-des-lieux");
  // Catégorie « Référent local » : incluse dans toutes les formules (Gratuit, Premium, Pro) pour les bailleurs.
  const REFERENT_KEYS = ["cat_referent", "/app/referents", "/app/referent-missions", "/app/referent-releves", "/app/referent-profil"];
  const referentPlans = ["Gratuit", "Premium", "Pro"];
  const referentIncluded = role === "admin" || role === "referent" || referentPlans.includes(user?.plan || "Gratuit");
  // Suivent ensuite strictement la configuration par profil définie par l'administrateur.
  if (!referentIncluded) {
    visibleKeys = visibleKeys.filter((k) => !REFERENT_KEYS.includes(k));
  }
  const isMgmt = MGMT_ROLES.includes(role);
  const strAllowed = isMgmt && visibleKeys.includes("cat_saisonnier");
  const strOn = strAllowed && !!user?.activities?.saisonnier;
  const visibleSet = new Set(visibleKeys.filter((k) => k !== "cat_saisonnier" && !STR_KEYS.includes(k)));
  if (strOn) visibleKeys.filter((k) => k === "cat_saisonnier" || STR_KEYS.includes(k)).forEach((k) => visibleSet.add(k));
  const longOff = strOn && user?.activities?.longue === false && visibleSet.has("/app/saisonnier");
  const menuTree = longOff ? [...MENU_TREE.filter((n) => n.key !== "cat_gestion").flatMap((n) => (n.key === "cat_saisonnier" ? [n, MENU_TREE.find((x) => x.key === "cat_gestion")] : [n]))] : MENU_TREE;
  if (role === "admin") visibleSet.add("/app/admin");
  const flatNAV = visibleKeys.map((k) => MENU_ITEMS[k]).filter(Boolean);
  if (role === "admin" && !flatNAV.some((n) => n.to === "/app/admin")) flatNAV.push(MENU_ITEMS["/app/admin"]);
  const toggleCat = (key) => setOpenCats((s) => ({ ...s, [key]: !isCatOpen(key) }));
  const isCatOpen = (key) => {
    if (key in openCats) return openCats[key];
    // ouvert par défaut si la catégorie contient la page active
    const node = MENU_TREE.find((n) => n.key === key);
    if (key === "cat_saisonnier" && location.pathname.startsWith("/app/saisonnier")) return true;
    return !!(node && node.children && node.children.includes(location.pathname));
  };

  const initials = (user?.name || user?.email || "U")
    .split(/[\s@._]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

  const handleLogout = async () => { await logout(); navigate("/"); };

  const SidebarContent = (
    <div className="flex flex-col h-full">
      <div className="h-[72px] flex items-center gap-2 px-6 border-b border-brand-soft">
        <BrandLogo variant="clair" className="h-7 w-auto" />
      </div>
      {strAllowed && <ActivitySwitch />}
      <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {!isMgmt && flatNAV.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-${n.to.replace(/\//g, "-").replace(/^-/, "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-[15px] transition-colors ${
                  isActive ? "bg-brand-lime text-brand-greenDeep" : "text-brand-green/70 hover:bg-brand-softer"
                }`
              }
            >
              <Icon className="w-5 h-5" />
              <span className="flex-1">{n.label}</span>
              {msgUnreadFor(n.to) > 0 && (
                <span data-testid={`msg-unread-badge-${n.to.replace(/\//g, "-").replace(/^-/, "")}`} className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{msgUnreadFor(n.to) > 9 ? "9+" : msgUnreadFor(n.to)}</span>
              )}
              {n.badge && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-greenDeep text-brand-lime">{n.badge}</span>
              )}
            </NavLink>
          );
        })}

        {isMgmt && menuTree.map((node) => {
          if (node.type === "item") {
            if (!visibleSet.has(node.key)) return null;
            const item = MENU_ITEMS[node.key];
            if (!item) return null;
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMobileOpen(false)}
                data-testid={`nav-${item.to.replace(/\//g, "-").replace(/^-/, "")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-[15px] transition-colors ${
                    isActive ? "bg-brand-lime text-brand-greenDeep" : "text-brand-green/70 hover:bg-brand-softer"
                  }`
                }
              >
                <Icon className="w-5 h-5" />
                <span className="flex-1">{item.label}</span>
              </NavLink>
            );
          }
          // catégorie
          if (!visibleSet.has(node.key)) return null;
          const childKeys = node.children.filter((c) => visibleSet.has(c));
          if (childKeys.length === 0) return null;
          const CatIcon = node.icon;
          const open = isCatOpen(node.key);
          const catActive = node.children.includes(location.pathname);
          return (
            <div key={node.key} className="space-y-0.5">
              <button
                type="button"
                onClick={() => toggleCat(node.key)}
                data-testid={`nav-cat-${node.key}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-semibold text-[15px] transition-colors ${
                  catActive && !open ? "text-brand-greenDeep bg-brand-softer" : "text-brand-green/70 hover:bg-brand-softer"
                }`}
              >
                <CatIcon className="w-5 h-5 flex-shrink-0" />
                <span className="flex-1 text-left leading-tight">{node.label}</span>
                {node.key === "cat_saisonnier" && strAccess === false && <LockIcon className="w-3.5 h-3.5 text-brand-green/50" data-testid="str-menu-lock" />}
                <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
              </button>
              {open && (
                <div className="ml-3 pl-3 border-l border-brand-soft space-y-0.5">
                  {childKeys.map((ck) => {
                    const item = MENU_ITEMS[ck];
                    if (!item) return null;
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        onClick={() => setMobileOpen(false)}
                        data-testid={`nav-${item.to.replace(/\//g, "-").replace(/^-/, "")}`}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                            isActive ? "bg-brand-lime text-brand-greenDeep" : "text-brand-green/65 hover:bg-brand-softer"
                          }`
                        }
                      >
                        <Icon className="w-4 h-4" />
                        <span className="flex-1">{item.label}</span>
                        {msgUnreadFor(item.to) > 0 && (
                          <span data-testid={`msg-unread-badge-${item.to.replace(/\//g, "-").replace(/^-/, "")}`} className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{msgUnreadFor(item.to) > 9 ? "9+" : msgUnreadFor(item.to)}</span>
                        )}
                        {item.badge && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-greenDeep text-brand-lime">{item.badge}</span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div className="p-3 border-t border-brand-soft">
        <NavLink to="/app/profil" onClick={() => setMobileOpen(false)} className={({ isActive }) => `w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${isActive ? "bg-brand-lime text-brand-greenDeep" : "text-brand-green/70 hover:bg-brand-softer"}`} data-testid="nav-profil">
          <CreditCard className="w-5 h-5" /> Mon abonnement
        </NavLink>
        <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-brand-green/70 hover:bg-brand-softer font-medium transition-colors">
          <LogOut className="w-5 h-5" /> Déconnexion
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-brand-softer">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-white border-r border-brand-soft z-40">
        {SidebarContent}
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-white h-full">{SidebarContent}</div>
          <div className="flex-1 bg-black/40" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-30 h-[72px] bg-white/90 backdrop-blur border-b border-brand-soft flex items-center justify-between px-5 lg:px-8">
          <div className="flex items-center gap-4">
            <button className="lg:hidden text-brand-greenDeep" onClick={() => setMobileOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="font-display text-2xl font-semibold text-brand-greenDeep">
              {TITLES[location.pathname] || (location.pathname.startsWith("/app/saisonnier/biens") ? "Bien saisonnier" : "360immoinvest")}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-softer w-64">
              <Search className="w-4 h-4 text-brand-green/50" />
              <input placeholder="Rechercher..." className="bg-transparent outline-none text-sm w-full text-brand-green" />
            </div>
            <button onClick={() => setContactOpen(true)} className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold text-sm transition-colors" data-testid="contact-btn" title="Contacter l'administrateur du site">
              <MessagesSquare className="w-4 h-4" /> Contact
            </button>
            <a href="mailto:support@360immoinvest.com" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold text-sm transition-colors" data-testid="aide-btn" title="Besoin d'aide ?">
              <LifeBuoy className="w-4 h-4" /> Aide
            </a>
            <NotificationBell role={user?.role} />
            <button onClick={() => { navigate("/app/profil"); setMobileOpen(false); }} className="flex items-center gap-2 pl-2 rounded-full hover:bg-brand-softer py-1 pr-2 transition-colors" data-testid="user-panel-btn" title="Mon compte">
              <span className="w-10 h-10 rounded-full bg-brand-lime flex items-center justify-center font-semibold text-brand-greenDeep">
                {initials}
              </span>
              <div className="hidden md:block leading-tight text-left">
                <p className="text-sm font-semibold text-brand-greenDeep">{user?.name}</p>
                <p className="text-xs text-brand-green/50">{user?.plan}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-brand-green/50 hidden md:block" />
            </button>
          </div>
        </header>

        <main className="p-5 lg:p-8">
          {location.pathname.startsWith("/app/saisonnier") && strAccess === null ? <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-brand-green/40" /></div>
            : strAccess === false && location.pathname.startsWith("/app/saisonnier") ? <StrLockedPreview /> : <Outlet />}
            <OnboardingHost user={user} ready={!(strAllowed && user && !user.activities_asked)} />
        </main>
      </div>
      <ContactAdminModal open={contactOpen} onClose={() => setContactOpen(false)} />
      <InstallBanner />
      <IosInstallGuide />
      {strAllowed && role !== "admin" && user && !user.activities_asked && <ActivityGate />}
    </div>
  );
}
